import React from "react";
import { NavLink, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { cn } from "@/lib/utils";
import {
  LayoutDashboard, Building2, Users, ShieldCheck, KeyRound, GitBranch,
  ScrollText, Settings, Bell, Network, X, Layers, ShoppingCart, Package,
} from "lucide-react";

const groups = (t, can) => [
  { items: [{ to: "/dashboard", label: t("dashboard"), icon: LayoutDashboard, perm: "dashboard.view", testId: "sidebar-dashboard" }] },
  {
    title: t("salesInventory"),
    items: [
      { to: "/sales", label: t("sales"), icon: ShoppingCart, perm: "sales.view", testId: "sidebar-sales" },
      { to: "/products", label: t("products"), icon: Package, perm: "products.view", testId: "sidebar-products" },
    ],
  },
  {
    title: t("organization"),
    items: [
      { to: "/branches", label: t("branches"), icon: Building2, perm: "branches.view", testId: "sidebar-branches" },
      { to: "/organization", label: t("orgProfile"), icon: Layers, perm: "settings.view", testId: "sidebar-organization" },
    ],
  },
  {
    title: t("usersAccess"),
    items: [
      { to: "/users", label: t("users"), icon: Users, perm: "users.view", testId: "sidebar-users" },
      { to: "/roles", label: t("roles"), icon: GitBranch, perm: "roles.view", testId: "sidebar-roles" },
      { to: "/permissions", label: t("permissions"), icon: KeyRound, perm: "permissions.view", testId: "sidebar-permissions" },
      { to: "/access-control", label: t("accessControl"), icon: Network, perm: "access_control.view", testId: "sidebar-access-control" },
    ],
  },
  {
    title: t("monitoring"),
    items: [
      { to: "/audit-logs", label: t("auditLogs"), icon: ScrollText, perm: "audit_logs.view", testId: "sidebar-audit-logs" },
    ],
  },
  {
    title: t("configuration"),
    items: [
      { to: "/notifications", label: t("notifications"), icon: Bell, perm: "notifications.view", testId: "sidebar-notifications" },
      { to: "/settings", label: t("settings"), icon: Settings, perm: "settings.view", testId: "sidebar-settings" },
    ],
  },
];

export function Sidebar({ open, onClose }) {
  const { t, can, user } = useApp();
  const location = useLocation();

  const content = (
    <div className="h-full flex flex-col bg-[hsl(var(--sidebar))] text-[hsl(var(--sidebar-foreground))]">
      <div className="h-16 flex items-center gap-3 px-5 border-b border-white/10 shrink-0">
        <div className="h-10 w-10 rounded-md bg-white flex items-center justify-center overflow-hidden shrink-0">
          <img src="/mobilezone-logo.png" alt="MobileZone" className="h-full w-full object-contain" />
        </div>
        <div className="min-w-0">
          <p className="font-heading font-bold text-sm leading-tight truncate">{t("appName")}</p>
          <p className="text-[10px] text-white/50 uppercase tracking-wider">{t("orgSuite")}</p>
        </div>
        <button className="ml-auto lg:hidden text-white/70" onClick={onClose} data-testid="sidebar-close-btn"><X className="h-5 w-5" /></button>
      </div>
      <nav className="flex-1 overflow-y-auto py-4 px-3 space-y-5">
        {groups(t, can).map((g, gi) => {
          const visible = g.items.filter((i) => can(i.perm));
          if (!visible.length) return null;
          return (
            <div key={gi}>
              {g.title && <p className="px-3 mb-1.5 text-[10px] font-semibold uppercase tracking-wider text-white/40">{g.title}</p>}
              <div className="space-y-0.5">
                {visible.map((item) => (
                  <NavLink
                    key={item.to} to={item.to} onClick={onClose} data-testid={item.testId}
                    className={({ isActive }) => cn(
                      "flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors duration-150",
                      isActive ? "bg-primary text-white" : "text-white/70 hover:bg-white/10 hover:text-white"
                    )}
                  >
                    <item.icon className="h-4 w-4 shrink-0" />
                    <span className="truncate">{item.label}</span>
                  </NavLink>
                ))}
              </div>
            </div>
          );
        })}
      </nav>
      <div className="p-4 border-t border-white/10 text-[10px] text-white/40">MobileZone · v1.0.0</div>
    </div>
  );

  return (
    <>
      <aside className="hidden lg:block w-64 shrink-0 h-screen sticky top-0">{content}</aside>
      {open && (
        <div className="fixed inset-0 z-50 lg:hidden">
          <div className="absolute inset-0 bg-black/50" onClick={onClose} />
          <div className="absolute left-0 top-0 h-full w-64">{content}</div>
        </div>
      )}
    </>
  );
}
